/**
 * Announcement bar: countdown timer + inline popup + Klaviyo email capture.
 * Scoped, event-delegated, safe to re-run on shopify:section:load.
 */
(function () {
  'use strict';

  var KLAVIYO_REVISION = '2026-07-15';
  var timerIntervals = new WeakMap();

  function pad(num) {
    return String(Math.max(0, num)).padStart(2, '0');
  }

  function setSegment(root, selector, value) {
    var el = root.querySelector(selector + ' .announcement-bar-timer-value');
    if (el) el.textContent = pad(value);
  }

  function tickTimer(el) {
    var endValue = el.getAttribute('data-end');
    var end = new Date(endValue).getTime();

    if (isNaN(end)) {
      el.hidden = true;
      return;
    }

    var remaining = end - Date.now();

    if (remaining <= 0) {
      var interval = timerIntervals.get(el);
      if (interval) {
        window.clearInterval(interval);
        timerIntervals.delete(el);
      }

      var behavior = el.getAttribute('data-expired-behavior');
      var live = el.querySelector('.announcement-bar-timer-live');
      var expired = el.querySelector('.announcement-bar-timer-expired');

      if (behavior === 'show_text') {
        if (live) live.hidden = true;
        if (expired) expired.hidden = false;
      } else {
        el.hidden = true;
      }
      return;
    }

    var totalSeconds = Math.floor(remaining / 1000);
    var days = Math.floor(totalSeconds / 86400);
    var hours = Math.floor((totalSeconds % 86400) / 3600);
    var minutes = Math.floor((totalSeconds % 3600) / 60);
    var seconds = totalSeconds % 60;

    setSegment(el, '[data-timer-days]', days);
    setSegment(el, '[data-timer-hours]', hours);
    setSegment(el, '[data-timer-minutes]', minutes);
    setSegment(el, '[data-timer-seconds]', seconds);
  }

  function initTimers(scope) {
    var timers = scope.querySelectorAll('[data-announcement-timer]');
    timers.forEach(function (el) {
      if (timerIntervals.has(el)) return;
      tickTimer(el);
      var interval = window.setInterval(function () {
        tickTimer(el);
      }, 1000);
      timerIntervals.set(el, interval);
    });
  }

  function syncStickyHeaderOffset() {
    var header = document.querySelector('[data-site-header]');
    if (!header) return;

    var bar = document.querySelector('[data-announcement-bar].is-sticky');
    if (bar && getComputedStyle(header).position.indexOf('sticky') > -1) {
      header.style.top = bar.getBoundingClientRect().height + 'px';
    } else {
      header.style.top = '';
    }
  }

  function isPanelOpen(panel) {
    return panel.classList.contains('is-open');
  }

  function syncOpenState() {
    var anyOpen = Array.prototype.some.call(
      document.querySelectorAll('[data-announcement-popup]'),
      isPanelOpen
    );

    var alreadyLocked = document.documentElement.style.overflow === 'hidden';

    if (anyOpen && !alreadyLocked) {
      var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.paddingRight = scrollbarWidth > 0 ? scrollbarWidth + 'px' : '';
    } else if (!anyOpen) {
      document.documentElement.style.overflow = '';
      document.documentElement.style.paddingRight = '';
    }

    var overlay = document.querySelector('[data-announcement-overlay]');
    if (overlay) overlay.classList.toggle('is-open', anyOpen);
  }

  function closeAllPopups(except) {
    document.querySelectorAll('[data-announcement-popup]').forEach(function (panel) {
      if (panel === except) return;
      panel.classList.remove('is-open');
      var trigger = document.querySelector('[aria-controls="' + panel.id + '"]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
    syncOpenState();
  }

  function handleToggleClick(trigger) {
    var panelId = trigger.getAttribute('aria-controls');
    var panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;

    var willOpen = !isPanelOpen(panel);
    closeAllPopups(willOpen ? panel : null);
    panel.classList.toggle('is-open', willOpen);
    trigger.setAttribute('aria-expanded', String(willOpen));
    syncOpenState();

    if (willOpen) {
      var input = panel.querySelector('input[type="email"]');
      if (input) input.focus();
    }
  }

  function setFormBusy(form, busy) {
    var submit = form.querySelector('.announcement-bar-popup-submit');
    var emailInput = form.querySelector('input[type="email"]');
    if (submit) submit.disabled = busy;
    if (emailInput) emailInput.disabled = busy;
    form.classList.toggle('is-submitting', busy);
  }

  function showFormMessage(form, message, isError) {
    var el = form.querySelector('[data-form-message]');
    if (!el) return;
    el.textContent = message;
    el.classList.toggle('is-error', !!isError);
    el.classList.toggle('is-success', !isError);
  }

  function subscribeToKlaviyo(publicKey, listId, email) {
    var url = 'https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(publicKey);
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED'
                    }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId
            }
          }
        }
      }
    };

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(body)
    });
  }

  function handleFormSubmit(form, event) {
    event.preventDefault();

    var honeypot = form.querySelector('.announcement-bar-popup-honeypot');
    if (honeypot && honeypot.value) return;

    var emailInput = form.querySelector('input[type="email"]');
    var email = emailInput ? emailInput.value.trim() : '';
    var publicKey = form.getAttribute('data-klaviyo-public-key');
    var listId = form.getAttribute('data-klaviyo-list-id');
    var successMessage = form.getAttribute('data-success-message') || 'Thanks!';
    var errorMessage = form.getAttribute('data-error-message') || 'Something went wrong. Please try again.';

    if (!email || (emailInput && !emailInput.checkValidity())) {
      showFormMessage(form, 'Please enter a valid email address.', true);
      return;
    }
    if (!publicKey || !listId) {
      showFormMessage(form, 'This form is not connected to Klaviyo yet.', true);
      return;
    }

    setFormBusy(form, true);
    showFormMessage(form, '', false);

    subscribeToKlaviyo(publicKey, listId, email)
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Klaviyo subscription failed: ' + response.status);
        }
        showFormMessage(form, successMessage, false);
        form.reset();
        var fields = form.querySelector('.announcement-bar-popup-fields');
        if (fields) fields.hidden = true;
      })
      .catch(function () {
        showFormMessage(form, errorMessage, true);
      })
      .finally(function () {
        setFormBusy(form, false);
      });
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-announcement-toggle]');
    if (trigger) {
      handleToggleClick(trigger);
      return;
    }

    if (event.target.closest('[data-announcement-close]')) {
      closeAllPopups();
      return;
    }

    if (!event.target.closest('[data-announcement-popup]') && !event.target.closest('[data-announcement-toggle]')) {
      closeAllPopups();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAllPopups();
  });

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-klaviyo-form]');
    if (form) handleFormSubmit(form, event);
  });

  document.addEventListener('DOMContentLoaded', function () {
    initTimers(document);
    syncStickyHeaderOffset();
  });

  document.addEventListener('shopify:section:load', function (event) {
    initTimers(event.target);
    syncStickyHeaderOffset();
  });

  window.addEventListener('resize', syncStickyHeaderOffset);

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    initTimers(document);
    syncStickyHeaderOffset();
  }
})();
